import type {
  AutoTradeDrawerParams,
  GamePlayerProjectionScore
} from '@library/types/scoring';
import { Checkbox, ProjectionInput } from '@library/components/inputs';
import {
  PlayerImage,
  PlayerName,
  PlayerProfile
} from '@library/components/player';
import { classNames, isStandard } from '@library/utils';
import { AutoTradeIndicator } from '@library/components/AutoTradeIndicator';
import { DateTime } from 'luxon';
import { GameModesSelect } from '@library/components/selects/GameModesSelect';
import { InGameIndicator } from '@library/components/InGameIndicator';
import type { LeagueInfo } from '@library/types/leagues';
import { OddsTypeIndicator } from '@library/components/OddsTypeIndicator';
import { PrizepicksSwitch } from '@library/components/switches';
import { PromoIndicator } from '@prizepicks/component-library/src/components/PromoIndicator';
import { ScoringCell } from '@library/components/scoring';
import { ScoringKebabMenu } from '@library/components/menus';
import type { Status } from '@library/types/projection';
import { StatusSelect } from '@library/components/selects';
import { Tooltip } from '@library/components/tooltip/Tooltip';
import { formatWithTimezoneChange } from '@library/utils/dateTime';
import { getDurationDisplayForLeague } from '@bo-utils/leagues';
import { useGameModes } from '@bo-hooks/gameModes/useGameModes';
import { useMemo } from 'react';
import { useSplitFlag } from '@bo-hooks/useSplitFlag';

export interface ScoringRowProps {
  change?: Partial<GamePlayerProjectionScore>;
  projection: GamePlayerProjectionScore;
  leagueInfo: LeagueInfo;
  updateProjection: ({
    change,
    initialProjection
  }: {
    change: Partial<GamePlayerProjectionScore>;
    initialProjection: GamePlayerProjectionScore;
  }) => void;
  handleShowHistoryDrawer: (params: AutoTradeDrawerParams) => void;
}

export const ScoringRow = ({
  change = {},
  projection,
  leagueInfo,
  updateProjection,
  handleShowHistoryDrawer
}: ScoringRowProps) => {
  const displayProjection = {
    ...projection,
    ...change
  };

  const {
    // autoFinalize,
    autoTrade,
    description,
    durationId,
    hasSuggestedLines,
    imageUrl,
    inGame,
    gameModeIds,
    isFinal,
    isOffTheBoard,
    isPromo,
    leagueId,
    oddsType,
    optIn = false,
    projectionValue,
    projectionId,
    startTime,
    statType,
    status,
    score,
    scoreId,
    scoreDetails,
    name,
    team,
    reboot,
    updatedAt
  } = displayProjection;

  const { gameModeSelectOptions: gameModeOptions } = useGameModes({});
  const gameModeValues = useMemo(
    () => gameModeIds?.map((id) => String(id)) ?? [],
    [gameModeIds]
  );

  const isUneditable = !!projection.isFinal;

  const isValueChanged = Number(projection.score) !== Number(score);

  const formattedStartTime = formatWithTimezoneChange(
    DateTime.fromISO(startTime),
    'D t ZZZZ'
  );

  const formattedUpdatedTime = formatWithTimezoneChange(
    DateTime.fromISO(updatedAt),
    't ZZZZ D'
  );

  const formattedScoreDetails = () => {
    if (scoreDetails === null) {
      return 'No score details';
    }
    const replacer = (key: string, value: string | number) => {
      if (key === 'PlayerName' || key === 'IsFinal' || key === 'UpdatedAt') {
        return undefined;
      }
      return value;
    };
    return scoreDetails
      ? JSON.stringify(scoreDetails, replacer, 4)
          .replace(/["{},]/g, '')
          .replace(/^\n/g, '')
          .replace(/^\s*[\r\n]/gm, '')
      : undefined;
  };

  const showOdds = !isStandard(oddsType);

  const handleChangeIsOffTheBoard = ({ checked }: { checked: boolean }) => {
    updateProjection({
      change: { isOffTheBoard: checked },
      initialProjection: projection
    });

    if (checked) {
      updateProjection({
        change: { isFinal: checked },
        initialProjection: projection
      });
    }
  };

  // const handleChangeAutoFinalize = (value: boolean) => {
  //   updateProjection({
  //     change: { autoFinalize: value },
  //     initialProjection: projection
  //   });
  // };

  const handleChangeIsFinal = (value: boolean) => {
    updateProjection({
      change: { isFinal: value },
      initialProjection: projection
    });

    if (isOffTheBoard && !value) {
      updateProjection({
        change: { isOffTheBoard: value },
        initialProjection: projection
      });
    }
  };

  const durationDisplay = getDurationDisplayForLeague({
    durationId: durationId ?? '',
    leagueId: leagueId ?? '',
    leagueInfo
  });

  const { isActive: isAFXActive } = useSplitFlag({
    flagName: 'AUTO_TRADER_ENABLED'
  });

  return (
    <tr
      className={classNames(
        'border-soFresh-140 text-soClean-110 hover:bg-soFresh-120 h-14 items-center border-b pr-1',
        isUneditable ? 'bg-soFresh-100' : ''
      )}
    >
      <ScoringCell className="pl-4" isOffTheBoard={isOffTheBoard} sortable>
        <PlayerProfile name={name} imageUrl={imageUrl}>
          <PlayerImage className="bg-soFresh-120 grid size-10 rounded-md placeholder:h-14 placeholder:w-10" />

          <PlayerName className="inline-flex" />
        </PlayerProfile>
      </ScoringCell>

      <ScoringCell isOffTheBoard={isOffTheBoard}>{durationDisplay}</ScoringCell>

      <ScoringCell isOffTheBoard={isOffTheBoard} sortable>
        {team}
      </ScoringCell>

      <ScoringCell isOffTheBoard={isOffTheBoard} sortable>
        {description}
      </ScoringCell>

      <ScoringCell isOffTheBoard={isOffTheBoard} hideOnSmallScreen sortable>
        {projectionId}
      </ScoringCell>

      <ScoringCell isOffTheBoard={isOffTheBoard} sortable>
        <span>{formattedStartTime}</span>
      </ScoringCell>

      <ScoringCell isOffTheBoard={isOffTheBoard} sortable>
        <div className="flex flex-row gap-2">
          <div className="flex flex-col gap-1 leading-3">
            <StatusSelect
              containerClassName="!h-6"
              className="!h-6 !min-h-0 bg-transparent !py-0 !pl-0 !pr-6"
              label="x"
              onChange={(value: Status) =>
                updateProjection({
                  change: { status: value },
                  initialProjection: projection
                })
              }
              value={status}
            />
            {reboot && (
              <span className="text-atlien-100 text-2xs">Rebooted!</span>
            )}
          </div>
        </div>
      </ScoringCell>

      <ScoringCell isOffTheBoard={isOffTheBoard} sortable>
        <div className="flex flex-row gap-2">
          <div className="flex flex-col gap-1 leading-3">
            <GameModesSelect
              gameModeOptions={gameModeOptions}
              gameModeValues={gameModeValues}
              onChange={() => {}}
              height="h-6"
              transparentBackground
              savingDisabled
            />
          </div>
        </div>
      </ScoringCell>
      <ScoringCell isOffTheBoard={isOffTheBoard} sortable>
        <Tooltip className="whitespace-pre-wrap" text={formattedScoreDetails()}>
          {statType}
        </Tooltip>
      </ScoringCell>

      <ScoringCell isOffTheBoard={isOffTheBoard} sortable>
        <div className="flex items-center gap-x-1.5">
          <span>{projectionValue}</span>
          <div
            className={classNames(
              'flex items-center gap-x-1.5',
              !isPromo && !showOdds ? 'hidden' : ''
            )}
          >
            {isPromo && <PromoIndicator className="size-4" optIn={optIn} />}
            {showOdds && (
              <OddsTypeIndicator className="size-4" oddsType={oddsType} />
            )}
          </div>
          {isAFXActive && (
            <>
              <AutoTradeIndicator
                autoTrade={autoTrade}
                hasSuggestedLines={hasSuggestedLines}
              />
              {inGame && (
                <InGameIndicator autoTrade={autoTrade} inGame={inGame} />
              )}
            </>
          )}
        </div>
      </ScoringCell>

      <ScoringCell isOffTheBoard={isOffTheBoard}>{scoreId}</ScoringCell>
      <ScoringCell isOffTheBoard={isOffTheBoard}>
        <ProjectionInput
          className={classNames(
            'focus:border-msJackson !text-2xs !h-6 w-8 rounded-md xl:!h-9 xl:w-14 xl:!text-sm',
            isValueChanged ? '!border-msJackson' : 'border-soFresh-140'
          )}
          disabled={isUneditable}
          id={projectionId}
          onValueChange={(value: string) => {
            updateProjection({
              change: { score: Number(value) },
              initialProjection: projection
            });
          }}
          value={score}
          testId={`${projectionId}-actual_score`}
        />
      </ScoringCell>

      <ScoringCell
        className="max-w-8 !whitespace-normal"
        isOffTheBoard={isOffTheBoard}
        sortable
      >
        <span>{formattedUpdatedTime}</span>
      </ScoringCell>

      <ScoringCell>
        <Checkbox
          checked={isOffTheBoard ?? false}
          disabled={isUneditable}
          id={`${projectionId}-is_off_the_board`}
          onCheckboxChange={handleChangeIsOffTheBoard}
          data-testid={`${projectionId}-is_off_the_board`}
        />
      </ScoringCell>

      <ScoringCell>
        <PrizepicksSwitch
          active={!!isFinal}
          disabled={isUneditable}
          handleChange={handleChangeIsFinal}
          statType={statType}
          testId={`${projectionId}-is_final`}
        />
      </ScoringCell>

      {/* <ScoringCell>
        <PrizepicksSwitch
          active={!!autoFinalize}
          disabled={isUneditable}
          handleChange={handleChangeAutoFinalize}
          statType={statType}
          testId={`${projectionId}-auto_finalize`}
        />
      </ScoringCell> */}

      <ScoringCell>
        <ScoringKebabMenu
          disabled={false}
          handleShowHistoryDrawer={() =>
            handleShowHistoryDrawer({
              gameStartDateTime: startTime,
              newPlayerDescriptors: {
                playerDescription: '',
                playerImage: imageUrl || '',
                playerName: name,
                secondaryHeader: ''
              },
              newProjection: projection
            })
          }
        />
      </ScoringCell>
    </tr>
  );
};
